import type { ReactNode } from 'react';
import { PaginationLink } from 'common/components/PaginationLink';

type Props = {
  currentPage: number;
  totalPages: number;
  firstPageURL: string | null;
  lastPageURL: string | null;
  previousPageURL: string | null;
  nextPageURL: string | null;
  onPageChange?: (newPage: number) => unknown;
};

export function Pagination({
  currentPage = 1,
  totalPages,
  firstPageURL,
  lastPageURL,
  nextPageURL,
  previousPageURL,
  onPageChange
}: Props): ReactNode {
  const firstPageLink = (
    <PaginationLink
      onClick={onPageChange}
      page={1}
      className="button small secondary"
      href={firstPageURL ?? undefined}
    >
      «
    </PaginationLink>
  );

  const lastPageLink = (
    <PaginationLink
      onClick={onPageChange}
      page={totalPages}
      className="button small secondary"
      href={lastPageURL ?? undefined}
    >
      »
    </PaginationLink>
  );

  const previousPageLink = (
    <PaginationLink
      onClick={onPageChange}
      page={currentPage - 1}
      className="button small"
      href={previousPageURL ?? undefined}
      rel="prev"
    >
      {'‹ '}
      <span className="m-pagination-button-text">
        {__('search.pagination.previous')}
      </span>
    </PaginationLink>
  );

  const nextPageLink = (
    <PaginationLink
      onClick={onPageChange}
      page={currentPage + 1}
      className="button small"
      href={nextPageURL ?? undefined}
      rel="next"
    >
      <span className="m-pagination-button-text">
        {__('search.pagination.next')}
      </span>
      {' ›'}
    </PaginationLink>
  );

  // Each link is rendered only once its own URL is there. React drops a nullish
  // `href`, and the anchor left behind is styled as a button but carries no link
  // role and takes no keyboard focus, so it reads as an offer the page cannot
  // honour. A page whose URL is null is one this cannot navigate to, and leaving
  // the link out is what says so.
  if (totalPages > 1) {
    return (
      <div className="m-pagination-container">
        <ul className="m-pagination">
          {currentPage !== 1 && firstPageURL !== null && (
            <li className="m-pagination-first-page-link">{firstPageLink} </li>
          )}
          {currentPage !== 1 && previousPageURL !== null && (
            <li>{previousPageLink} </li>
          )}
          <li className="m-pagination-count">
            {__('search.pagination.page')} <strong>{currentPage}</strong> /{' '}
            {totalPages}
          </li>
          {currentPage !== totalPages && nextPageURL !== null && (
            <li> {nextPageLink}</li>
          )}
          {currentPage !== totalPages && lastPageURL !== null && (
            <li className="m-pagination-last-page-link"> {lastPageLink}</li>
          )}
        </ul>
      </div>
    );
  } else {
    return null;
  }
}
